import { PlayCircleIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { LessonResponse } from '../../../server/response/listeningResponseScheme';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import firebaseStore from '../../stores/firebaseStore';
import modalStore from '../../stores/modalStore';

/**
 * 초 단위 시간을 "X분" 형식으로 변환
 */
const formatDuration = (seconds: number): string => {
    const minutes = Math.round(seconds / 60);
    return `${minutes}분`;
};

class ViewStore {
    lesson: LessonResponse | null = null;
    isLoading = true;
    error: string | null = null;

    isMyLesson = false;
    isStarting = false;

    constructor(private lessonHash: string) {
        makeAutoObservable(this);
    }

    async fetch() {
        this.isLoading = true;
        this.error = null;

        try {
            const lesson = await apiClient.lesson.public.getLesson.query({
                lessonHash: this.lessonHash,
            });

            runInAction(() => {
                this.lesson = lesson;
                this.isLoading = false;
            });

            // 레슨이 존재하면 userLesson 확인
            if (lesson) {
                await this.checkUserLesson();
            }
        } catch {
            runInAction(() => {
                this.error = '레슨을 찾을 수 없습니다';
                this.isLoading = false;
            });
        }
    }

    async checkUserLesson() {
        const isLoggedIn = await firebaseStore.isLoggedIn();
        if (!isLoggedIn) {
            runInAction(() => {
                this.isMyLesson = false;
            });
            return;
        }

        try {
            const userLesson = await apiClient.userLesson.member.getUserLesson.query({
                lessonHash: this.lessonHash,
            });

            runInAction(() => {
                this.isMyLesson = !!userLesson;
            });
        } catch {
            runInAction(() => {
                this.isMyLesson = false;
            });
        }
    }

    setIsStarting(value: boolean) {
        this.isStarting = value;
    }
}

interface LessonIntroProps {
    lessonHash: string;
    /** 레슨 시작/계속 후 호출되는 콜백 (이동 처리) */
    onNavigate: () => void;
}

/**
 * 레슨 소개 UI 컴포넌트 (재사용 가능)
 * - LessonIntroPage, LessonIntroModalContent 등에서 사용
 * - 레슨 fetch, 로그인 확인, userLesson 존재 여부 확인, userLesson 생성을 내부에서 처리
 */
const LessonIntro = observer(({ lessonHash, onNavigate }: LessonIntroProps) => {
    const viewStore = useMobxInstance(ViewStore, lessonHash);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    const handleStartLesson = async () => {
        viewStore.setIsStarting(true);

        // 로그인 확인
        const isLoggedIn = await firebaseStore.isLoggedIn();
        if (!isLoggedIn) {
            const result = await modalStore.login();
            if (!result.returnValue) {
                viewStore.setIsStarting(false);
                return;
            }
            // 로그인 후 userLesson 다시 확인
            await viewStore.checkUserLesson();
        }

        // 내 레슨이 아니면 생성
        if (!viewStore.isMyLesson) {
            try {
                await apiClient.userLesson.member.createUserLessonFromLesson.mutate({
                    lessonHash,
                });
            } catch (e) {
                modalStore.error({ error: e });
                viewStore.setIsStarting(false);
                return;
            }
        }

        onNavigate();
    };

    // 로딩 상태
    if (viewStore.isLoading) {
        return (
            <div className="flex items-center justify-center py-10">
                <span className="loading loading-spinner loading-md text-base-content/50" />
            </div>
        );
    }

    // 에러 상태
    if (viewStore.error || !viewStore.lesson) {
        return (
            <div className="flex flex-col items-center justify-center gap-4 py-10">
                <p className="text-base-content/70">
                    {viewStore.error || '레슨을 찾을 수 없습니다'}
                </p>
            </div>
        );
    }

    const { lesson } = viewStore;

    return (
        <div className="w-full">
            {/* 썸네일 */}
            <div className="aspect-video w-full overflow-hidden rounded-lg">
                {lesson.video.thumbnail ? (
                    <img
                        src={lesson.video.thumbnail}
                        alt={lesson.titleKo || lesson.video.title}
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <div className="bg-base-300 flex h-full w-full items-center justify-center">
                        <PlayCircleIcon className="text-base-content/30 size-16" />
                    </div>
                )}
            </div>

            {/* 레슨 정보 */}
            <div className="mt-4">
                <h1 className="text-base-content text-xl font-medium">
                    {lesson.titleKo || lesson.title || lesson.video.title}
                </h1>
                {lesson.overviewKo && (
                    <p className="text-base-content/70 mt-3 text-sm">{lesson.overviewKo}</p>
                )}

                {/* 메타 정보 */}
                <div className="text-base-content/50 mt-4 flex items-center gap-4 text-sm">
                    {lesson.chapterCount && <span>챕터 {lesson.chapterCount}개</span>}
                    {lesson.totalDuration && <span>{formatDuration(lesson.totalDuration)}</span>}
                </div>
            </div>

            {/* 시작 버튼 */}
            <div className="mt-6">
                <button
                    className={twMerge('btn btn-action-primary btn-lg w-full')}
                    onClick={handleStartLesson}
                    disabled={viewStore.isStarting}
                >
                    {viewStore.isStarting ? (
                        <span className="loading loading-spinner loading-sm" />
                    ) : (
                        <PlayCircleIcon className="size-5 stroke-[1.5]" />
                    )}
                    {viewStore.isMyLesson ? '계속 학습하기' : '레슨 시작하기'}
                </button>
            </div>
        </div>
    );
});

export default LessonIntro;
